/** @jsxImportSource ./simboluri-jsx */
import { useMemo } from "react";
import { useT, useTn } from "../i18n";

// AVERTISMENTELE PASULUI 4 — op-uri pe care validarea geometrică le-a refuzat.
//
// Serverul le trimite ca PROPOZIȚII (`model.py` le compune în ~50 de locuri cu
// f-string-uri), iar interfața le lipea cu „; " într-un paragraf: 20 de
// avertismente deveneau un bloc de 1.600 de caractere fără nicio pauză, în
// care aceeași cauză repetată de 17 ori arăta ca 17 probleme diferite.
//
// Aici se despart în (context, element, motiv, verdict) și se GRUPEAZĂ pe
// motiv identic: cele 17 rânduri de reancorare devin două, cu lista
// elementelor. Despicarea e pe „: ", cu elementul recunoscut după forma
// id-ului de plan („ST13", „GR1") sau după numele op-ului („adauga_stalp");
// dacă nu se recunoaște nimic, rândul păstrează textul întreg, ca să nu
// dispară informație într-o parsare ratată.
//
// Reparația DURABILĂ e ca `model.py` să emită structuri, nu propoziții —
// atunci parserul de mai jos dispare.
const RE_ID_PLAN = /^(ST|GR|PER|AX)\d+/;

type AvertismentPas4 = {
  context?: string;
  elemente: string[];
  motiv: string;
  verdict?: string;
  brut?: string; // când parsarea n-a recunoscut forma
};

function despicaAvertisment(a: string): AvertismentPas4 {
  let text = a.trim();
  let verdict: string | undefined;
  const m = text.match(/\s*—\s*(sărit|sărită|refuzat|refuzată)\.?$/i);
  if (m) {
    verdict = m[1].toLowerCase();
    text = text.slice(0, m.index).trim();
  } else if (/rămâne picat/i.test(text)) {
    verdict = "rămâne picat";
  }
  const parti = text.split(": ").map((x) => x.trim());
  // ultimul segment care arată ca un element sau ca un nume de op
  let iEl = -1;
  for (let i = parti.length - 2; i >= 0; i--) {
    if (RE_ID_PLAN.test(parti[i]) || /^[a-z]+(_[a-z]+)+$/.test(parti[i])) {
      iEl = i;
      break;
    }
  }
  if (iEl < 0) return { elemente: [], motiv: text, verdict, brut: a };
  // contextul poartă uneori chiar verdictul („reancorare, rămâne picat") —
  // altfel același cuvânt ar apărea în două coloane
  let context: string | undefined =
    parti.slice(0, iEl).join(" · ") || undefined;
  const mc = context?.match(/^(.*),\s*(rămâne picat|nu s-a putut aplica)$/);
  if (mc) {
    context = mc[1];
    verdict = mc[2];
  }
  return {
    context,
    elemente: [parti[iEl]],
    motiv: parti.slice(iEl + 1).join(": "),
    verdict,
  };
}

function grupeazaAvertismente(brute: string[]): AvertismentPas4[] {
  const dupaMotiv = new Map<string, AvertismentPas4>();
  for (const a of brute) {
    const d = despicaAvertisment(a);
    const cheie = `${d.context ?? ""}|${d.motiv}|${d.verdict ?? ""}`;
    const deja = dupaMotiv.get(cheie);
    if (deja) deja.elemente.push(...d.elemente);
    else dupaMotiv.set(cheie, d);
  }
  return [...dupaMotiv.values()];
}

export default function TabelAvertismentePas4({
  brute,
  total,
}: {
  brute: string[];
  total?: number | null;
}) {
  const t = useT();
  const tn = useTn();
  const randuri = useMemo(() => grupeazaAvertismente(brute), [brute]);
  if (!randuri.length) return null;
  const tainuite = total != null && total > brute.length ? total - brute.length : 0;
  return (
    <details className="so-av" open>
      <summary>
        {t("struct.opuri.titlu", { n: total ?? brute.length })}
        {randuri.length < brute.length &&
          tn("struct.opuri.grupate#n", randuri.length)}
      </summary>
      <table className="sg-table so-avt">
        <thead>
          <tr>
            <th>{t("struct.opuri.col_elemente")}</th>
            <th>{t("struct.opuri.col_context")}</th>
            <th>{t("struct.opuri.col_motiv")}</th>
            <th>{t("struct.opuri.col_ce")}</th>
          </tr>
        </thead>
        <tbody>
          {randuri.map((r, i) => (
            <tr key={i}>
              {r.brut ? (
                <td colSpan={4}>{r.brut}</td>
              ) : (
                <>
                  <td>
                    <span className="sg-mono so-elem">
                      {r.elemente.join(", ")}
                    </span>
                    {r.elemente.length > 1 && (
                      <div className="sg-muted so-mic">
                        {tn("struct.opuri.aceeasi_cauza#n", r.elemente.length)}
                      </div>
                    )}
                  </td>
                  <td className="sg-muted">
                    {r.context ?? t("struct.opuri.aplicarea")}
                  </td>
                  <td>{r.motiv}</td>
                  <td className="sg-muted">{r.verdict ?? "—"}</td>
                </>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      {tainuite > 0 && (
        <div className="sg-muted so-mic">
          {tn("struct.opuri.tainuite#n", tainuite)}
        </div>
      )}
    </details>
  );
}

